// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

// MqttWebSocket.js - Robust Implementation
// Utility for connecting to MQTT over WebSocket and subscribing to health data topics
// Uses mqtt.js for proper MQTT protocol handling.

import mqtt from 'mqtt';
import { getConfigValue } from './config';

// Custom error class for MQTT connection errors
class MqttConnectionError extends Error {
  constructor(message, originalError = null) {
    super(message);
    this.name = 'MqttConnectionError';
    this.originalError = originalError;
    // Ensure the stack trace is captured properly
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, MqttConnectionError);
    }
  }
}

// Generate a unique client ID for each connection
const generateUniqueClientId = () => {
  // Always generate a fresh, unique client ID to prevent conflicts
  // This prevents issues with multiple tabs, refreshes, or stale sessions
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  const browserRandom = Math.random().toString(36).substring(2, 4);
  const sessionId = Math.floor(Math.random() * 10000).toString(16);

  // Include browser fingerprint for better uniqueness
  const browserFingerprint = navigator.userAgent.length.toString(16);

  const clientId = `dashboard_${timestamp}_${random}_${browserRandom}_${sessionId}_${browserFingerprint}`;

  console.log(`[MQTT] Generated unique client ID: ${clientId}`);
  return clientId;
};

export default class MqttWebSocket {
  constructor({
    url,
    topic,
    topics,
    username,
    password,
    clientId,
    onMessage,
    keepalive,
    connectTimeout,
    onDisconnect,
  }) {
    // Get broker host and port from config
    let brokerHost = getConfigValue('mqtt.brokerHost');
    const brokerPort = getConfigValue('mqtt.brokerPort', 9001);

    // Handle case where brokerHost might be an object or have extra characters
    if (typeof brokerHost === 'object') {
      brokerHost = window.location.hostname; // Default to current hostname if brokerHost is an object
    } else if (brokerHost && typeof brokerHost === 'string') {
      // Clean up the host string in case it has extra characters
      brokerHost = brokerHost.replace(/[^a-zA-Z0-9.-]/g, '');
    } else {
      brokerHost = window.location.hostname; // Default to current hostname if brokerHost is not a string
    }

    // Construct the WebSocket URL with protocol detection
    // Use WSS (secure WebSocket) if the page is loaded over HTTPS
    const isSecure = window.location.protocol === 'https:';
    const wsProtocol = isSecure ? 'wss' : 'ws';

    // Always use the proxied WebSocket endpoint through nginx
    // This works for both HTTP and HTTPS access
    this.url = url || `${wsProtocol}://${window.location.host}/ws/mqtt`;
    // Ensure we subscribe to both starfleet and wireguard topics
    const defaultTopics = [
      `${getConfigValue('mqtt.topicPrefix', 'starfleet')}/#`, // Starfleet status/health messages
      'wireguard/#', // WireGuard metrics from wg-monitor
      'starfleet/status/stargate_1', // Legacy specific topic
    ];
    this.topics = topics || (topic ? [topic, ...defaultTopics] : defaultTopics);

    this.username = username || getConfigValue('mqtt.username');
    this.password = password || getConfigValue('mqtt.password');
    this.onMessage = onMessage;
    this.onDisconnect = onDisconnect;
    this.client = null;
    this.connected = false;
    this.connectInProgress = false;
    this.connectionError = null;
    this.reconnectTimer = null;
    // Use the provided clientId if available, otherwise generate one
    this.clientId = clientId || generateUniqueClientId();

    // Keepalive: 120s balances fast dead-connection detection with tolerance
    // for browser timer throttling when the tab is backgrounded.  Browsers may
    // delay timers to once per minute; 120s gives a 180s (1.5x) broker window
    // which comfortably survives typical throttling.
    this.keepalive = keepalive || getConfigValue('mqtt.keepalive', 120);
    this.connectTimeout = connectTimeout || getConfigValue('mqtt.connectTimeout', 30000);
    this._reconnectAttempts = 0;
    this._maxReconnectAttempts = getConfigValue('mqtt.maxReconnectAttempts', 15);
    this._debug = getConfigValue('features.debugEnabled', true);

    // IMPROVED: Track last activity for health monitoring
    this._lastMessageTime = Date.now();
    this._lastPingTime = null;
    this._connectionHealthy = true;

    // Create a log function that can be toggled by debug configuration
    this.log = (message, isError = false) => {
      if (this._debug || isError) {
        if (isError) {
          console.error(`[MQTT] ${message}`);
        } else {
          // Reduce noise by only logging important messages
          if (
            message.includes('Connected') ||
            message.includes('error') ||
            message.includes('disconnect')
          ) {
            console.log(`[MQTT] ${message}`);
          }
        }
      }
    };
  }

  connect() {
    // If already connected or connection in progress, don't try to connect again
    if (this.connected || this.connectInProgress) {
      this.log(
        `Connect already ${this.connected ? 'connected' : 'in progress'}, skipping connection attempt`
      );
      return;
    }

    this.connectInProgress = true;

    this.log(`Connecting to MQTT broker at ${this.url}`);

    // Clear any existing reconnect timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Create connection options with robust parameters
    const options = {
      clientId: this.clientId,
      clean: true, // Use clean sessions for dashboard clients to avoid duplicate ID issues
      reconnectPeriod: 0, // We'll handle reconnection ourselves
      connectTimeout: this.connectTimeout,
      protocolVersion: 4, // MQTT v3.1.1
      keepalive: this.keepalive,
      rejectUnauthorized: false,
      // Add basic WebSocket options
      wsOptions: {
        handshakeTimeout: 5000, // 5 seconds for WebSocket handshake
        protocolVersion: 13, // WebSocket protocol version
      },
      // DISABLED: Dashboard clients should not publish will messages
      // will: {
      //   topic: `${getConfigValue('mqtt.topicPrefix', 'starfleet')}/clients/${this.clientId}/status`,
      //   payload: JSON.stringify({ status: 'offline', timestamp: new Date().toISOString() }),
      //   qos: 1,
      //   retain: false
      // }
    };

    // Only add username and password if they are provided
    if (this.username && this.password) {
      this.log(`Using credentials for user: ${this.username}`);
      options.username = this.username;
      options.password = this.password;
    } else {
      this.log('Using anonymous connection');
    }

    // Properly clean up any existing client before creating a new one
    this._cleanupExistingClient();

    try {
      this.log(`Creating new MQTT connection to ${this.url}...`);
      this.client = mqtt.connect(this.url, options);
      this.log(`MQTT client initialized with ID: ${this.clientId}`);

      // Define all event handlers
      this._setupEventHandlers();

      // Set a timeout for the initial connection
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
      }

      this.connectionTimeout = setTimeout(() => {
        if (!this.connected && this.connectInProgress) {
          this.log('Connection timeout, cleaning up...', true);
          const error = new Error(
            'Connection to MQTT broker timed out. The broker may be unreachable or not accepting connections.'
          );
          this.connectionError = error.message;
          this.connectInProgress = false;
          this._cleanupExistingClient();

          // If we have an onError handler, call it with the timeout error
          if (this.onError) {
            this.onError(error);
          }

          this._scheduleReconnect();
        }
      }, this.connectTimeout); // Use the configured connect timeout
    } catch (e) {
      this.log(`Error creating MQTT client: ${e.message}`, true);
      this.connectionError = e.message;
      this.connectInProgress = false;
      this._scheduleReconnect();
    }
  }

  // Helper method to clean up existing client
  _cleanupExistingClient() {
    if (this.client) {
      try {
        this.log('Cleaning up existing MQTT client before reconnection');
        // Remove all listeners to prevent memory leaks
        this.client.removeAllListeners();

        // End the connection forcefully
        this.client.end(true);
      } catch (e) {
        this.log(`Error cleaning up previous MQTT client: ${e.message}`, true);
      }
      this.client = null;
    }
  }

  // Set up all event handlers for the MQTT client
  _setupEventHandlers() {
    if (!this.client) return;

    // Store the original connect timeout so we can clear it
    this.connectionTimeout = null;

    // Connect event
    this.client.on('connect', () => {
      // Clear any pending connection timeout
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }

      this.connectInProgress = false;
      this.connected = true;
      this.connectionError = null;
      this.log('Successfully connected to MQTT broker!');

      // Reset reconnection attempts counter on successful connection
      this._reconnectAttempts = 0;

      // Notify parent component of connection
      if (this.onConnect) {
        this.onConnect();
      }

      // Publish online status and subscribe to topics
      this._publishStatus('online');
      // Always re-subscribe on connect/reconnect to ensure subscriptions are active
      this._subscribeToTopics();
    });

    // Message event
    this.client.on('message', (topic, message) => {
      // IMPROVED: Track last message time for health monitoring
      this._lastMessageTime = Date.now();
      this._connectionHealthy = true;

      if (this.onMessage) {
        try {
          let data;
          const rawData = message.toString();

          try {
            // Try to parse as JSON
            data = JSON.parse(rawData);
            // Add topic to the data object
            data.topic = topic;
          } catch (e) {
            // If not valid JSON, create a simple object
            data = {
              payload: rawData,
              topic: topic,
              timestamp: new Date().toISOString(),
            };
          }

          // Ensure we have a timestamp
          if (!data.timestamp) {
            data.timestamp = new Date().toISOString();
          }

          // Always call onMessage callback with the data
          this.onMessage(data);
        } catch (e) {
          this.log(`Error processing MQTT message: ${e.message}`, true);
          console.error('MQTT message processing error:', e);
        }
      }
    });

    // Error event
    this.client.on('error', (err) => {
      let errorMessage = err.message;
      let friendlyMessage = err.message;

      // Keepalive timeouts are expected when the browser tab is backgrounded
      // (JS timers are throttled).  Treat them as routine reconnection events.
      const isKeepaliveTimeout = errorMessage.includes('Keepalive timeout');

      if (isKeepaliveTimeout) {
        console.log('[MQTT] Keepalive timeout (likely tab was backgrounded) — will reconnect');
        this.connectionError = null;
        this.connected = false;
        this.connectInProgress = false;
        if (this.onError) {
          try {
            this.onError(new MqttConnectionError('Keepalive timeout', err));
          } catch (_) {}
        }
        return;
      }

      // Provide more user-friendly error messages for real errors
      if (errorMessage.includes('connack timeout')) {
        friendlyMessage =
          'Connection to MQTT broker timed out. Please check your network connection and ensure the MQTT broker is running.';
      } else if (errorMessage.includes('connect ECONNREFUSED')) {
        friendlyMessage = `Could not connect to MQTT broker at ${this.url}. The broker may be down or the address is incorrect.`;
      } else if (errorMessage.includes('WebSocket connection failed')) {
        friendlyMessage =
          'WebSocket connection to MQTT broker failed. Check if the broker supports WebSockets and CORS settings.';
      } else if (errorMessage.includes('Connection refused: Not authorized')) {
        friendlyMessage = 'Authentication failed. Please check your MQTT username and password.';
      }

      this.log(`MQTT WebSocket error: ${errorMessage}`, true);
      this.connectionError = friendlyMessage;
      this.connectInProgress = false;

      // Create a custom error with the friendly message and original error details
      const mqttError = new MqttConnectionError(friendlyMessage, err);

      // If we have an onError handler, call it with our custom error
      if (this.onError) {
        try {
          this.onError(mqttError);
        } catch (onErrorErr) {
          console.error('Error in MQTT onError handler:', onErrorErr);
        }
      }
    });

    // Offline event
    this.client.on('offline', () => {
      this.log('MQTT client is offline');
      this.connected = false;
      this.connectInProgress = false;
    });

    // Reconnect event (mqtt.js internal reconnect)
    this.client.on('reconnect', () => {
      this.log('mqtt.js internal reconnect event fired');
    });

    // Close event
    this.client.on('close', () => {
      this.log('MQTT connection closed');
      this.connected = false;
      this.connectInProgress = false;

      // Notify parent component of disconnection
      if (this.onDisconnect) {
        try {
          this.onDisconnect();
        } catch (err) {
          console.error('Error in onDisconnect handler:', err);
        }
      }

      // Only schedule reconnect if not explicitly disconnected
      if (this.client) {
        this._scheduleReconnect();
      }
    });

    // End event
    this.client.on('end', () => {
      this.log('MQTT client ended');
      this.connected = false;
      this.connectInProgress = false;
    });
  }

  // Schedule a reconnection with exponential backoff
  _scheduleReconnect() {
    // Clear any existing reconnect timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Reset connect in progress flag
    this.connectInProgress = false;

    // If we've exceeded max attempts, give up
    if (this._reconnectAttempts >= this._maxReconnectAttempts) {
      this.log(`Max reconnection attempts (${this._maxReconnectAttempts}) reached`, true);
      if (this.onError) {
        this.onError(new Error(`Failed to connect after ${this._maxReconnectAttempts} attempts`));
      }
      return;
    }

    // Use much longer delays to prevent rapid cycling
    const baseDelay = Math.min(5000 * Math.pow(1.5, this._reconnectAttempts), 30000);
    const jitter = Math.floor(Math.random() * 2000); // Add up to 2s jitter
    const reconnectDelay = baseDelay + jitter;

    this.log(
      `Scheduling reconnect attempt ${this._reconnectAttempts + 1}/${this._maxReconnectAttempts} in ${Math.round(reconnectDelay)}ms`
    );

    this.reconnectTimer = setTimeout(() => {
      this._reconnectAttempts++;
      this.log(
        `Reconnecting after connection close (attempt ${this._reconnectAttempts}/${this._maxReconnectAttempts})`
      );
      this.reconnectTimer = null;
      this.connect();
    }, reconnectDelay);
  }

  // Helper method to publish client status
  _publishStatus(status) {
    // DISABLED: Dashboard clients should not publish status messages
    // This prevents spurious messages in the MQTT broker
    return;
  }

  // Helper method to handle topic subscriptions
  _subscribeToTopics() {
    if (!this.client || !this.connected) {
      this.log('Cannot subscribe: not connected to MQTT broker', true);
      console.error('MQTT subscription failed: Not connected to broker');
      return;
    }

    // Subscribe to all topics in the list
    this.topics.forEach((topic) => {
      this.client.subscribe(topic, { qos: 1 }, (err) => {
        if (err) {
          this.log(`MQTT subscribe error for topic ${topic}: ${err.message}`, true);
          console.error(`Failed to subscribe to ${topic}:`, err);
        } else {
          this.log(`Subscribed to topic: ${topic}`);

          // Test message functionality is preserved but disabled by default
          // Can be re-enabled via the 'mqtt.enableTestMessages' config option
          if (
            (topic === '#' || topic.includes('#')) &&
            getConfigValue('mqtt.enableTestMessages', false)
          ) {
            // Only publish test message on initial connection, not on every reconnect
            // And only once per session to reduce noise
            const hasPublishedTest = sessionStorage.getItem('mqttTestPublished');

            if (!hasPublishedTest) {
              const testTopic = `${getConfigValue('mqtt.topicPrefix', 'starfleet')}/dashboard/test`;
              const testMessage = {
                type: 'TEST',
                message: 'Dashboard subscription test',
                timestamp: new Date().toISOString(),
                clientId: this.clientId,
              };

              // Small delay to ensure subscription is fully established
              setTimeout(() => {
                this.publish(testTopic, testMessage);

                // Mark that we've published the test to avoid repeating
                sessionStorage.setItem('mqttTestPublished', 'true');
              }, 1000);
            }
          } else {
          }
        }
      });
    });
  }

  // Public method to disconnect
  disconnect() {
    // Clear any reconnect timers
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.client) {
      this.log('Disconnecting from MQTT broker');

      // Publish offline status before disconnecting
      this._publishStatus('offline');

      try {
        // End the connection cleanly
        this.client.end(true);
      } catch (e) {
        this.log(`Error ending MQTT client: ${e.message}`, true);
      }

      this.client = null;
      this.connected = false;
      this.connectInProgress = false;
    }
  }

  // Public method to check connection status
  isConnected() {
    // IMPROVED: Also check if the underlying client is actually connected
    if (!this.connected) return false;
    if (!this.client) return false;

    // Check mqtt.js client state if available
    if (this.client.connected === false) {
      this.connected = false;
      return false;
    }

    return this.connected;
  }

  // Public method to get any connection error
  getConnectionError() {
    return this.connectionError;
  }

  // IMPROVED: Public method to get last message time for health monitoring
  getLastMessageTime() {
    return this._lastMessageTime;
  }

  // IMPROVED: Public method to check connection health
  isConnectionHealthy() {
    if (!this.isConnected()) return false;

    // If we haven't received a message in 2 minutes while connected, connection might be stale
    const timeSinceLastMessage = Date.now() - this._lastMessageTime;
    if (timeSinceLastMessage > 120000) {
      this._connectionHealthy = false;
    }

    return this._connectionHealthy;
  }

  // Public method to publish a message
  publish(topic, message) {
    if (!this.client || !this.connected) {
      this.log('Cannot publish: not connected to MQTT broker', true);
      return false;
    }

    try {
      const payload = typeof message === 'object' ? JSON.stringify(message) : message;
      this.client.publish(topic, payload, { qos: 1 });
      return true;
    } catch (error) {
      this.log(`Error publishing MQTT message: ${error.message}`, true);
      return false;
    }
  }
}
